import React, { forwardRef, Ref, SyntheticEvent, useMemo } from "react";
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteProps,
  AutocompleteRenderOptionState,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemProps,
  ListItemText,
  ListProps,
  Tooltip,
} from "@mui/material";
import differenceBy from "lodash/differenceBy";
import { SELECT_ALL_CHECKBOX_OPTION } from "components/shared/CheckboxTree/CheckboxTree.utils";
import Chip from "components/shared/Chip";
import TextField, { TTextFieldProps } from "components/shared/TextField";
import InfoTooltip from "../InfoTooltip";
import styles from "./ComboBox.styles";

export type TComboBoxOption<Id = string> = {
  id?: Id;
  label: string;
  value: string;
  type?: string;
  disabled?: boolean;
  helpText?: string;
  title?: string;
};

export type TComboBoxProps = Omit<
  AutocompleteProps<
    TComboBoxOption, // type of values
    true, // Multiple
    boolean, // DisableClearable
    false // FreeSolo
  >,
  "onChange" | "renderInput"
> & {
  error?: boolean;
  helperText?: string;
  label?: string;
  onChange?: (selectedOptions: TComboBoxOption[]) => void;
  options: TComboBoxOption[];
  required?: boolean;
  textFieldProps?: TTextFieldProps;
  selectAllOption?: boolean;
  deselectAllLabelText?: string;
  selectAllLabelText?: string;
  showSelectAllCount?: boolean;
};

const Option = (
  props: ListItemProps,
  option: TComboBoxOption,
  state: AutocompleteRenderOptionState,
) => {
  const elementId = `combobox-menu-option-${props.id}`;
  const { key, ...listItemProps } = props;

  return (
    <Tooltip title={option?.title} placement="right" key={elementId}>
      <ListItem
        key={key}
        data-qaid={elementId}
        {...listItemProps}
        sx={styles.option}
      >
        <Checkbox
          checked={state.selected}
          disabled={!!props["aria-disabled"]}
          key={`combobox-checkbox-option-${option.value}`}
        />
        <ListItemText key={`combobox-text-${option.value}`}>
          {option.label}
          {option.helpText && (
            <InfoTooltip
              title={option.helpText}
              sx={styles.notificationsTooltip}
            />
          )}
        </ListItemText>
      </ListItem>
    </Tooltip>
  );
};

const ComboBox = forwardRef((comboBoxProps: TComboBoxProps, ref: Ref<any>) => {
  // omitting custom `onChange` & `textFieldProps` props so that it doesn't conflict with the existing `onChange` prop
  const {
    error,
    helperText,
    onChange,
    textFieldProps,
    selectAllOption,
    deselectAllLabelText,
    selectAllLabelText,
    showSelectAllCount = true,
    value: currentValue = [],
    getOptionDisabled,
    ...autocompleteProps
  } = comboBoxProps;

  // Always put disabled values in the beginning
  const selectedValue = useMemo(
    () =>
      currentValue.sort((prev, next) => {
        const isPrevDisabled = getOptionDisabled?.(prev);
        const isNextDisabled = getOptionDisabled?.(next);

        if (isPrevDisabled && !isNextDisabled) {
          return -1;
        } else if (!isPrevDisabled && isNextDisabled) {
          return 1;
        } else {
          return 0;
        }
      }),
    [getOptionDisabled, currentValue],
  );

  const handleChange = (
    _event: SyntheticEvent<Element, Event>,
    nextValue: TComboBoxOption[],
    _reason: AutocompleteChangeReason,
  ) => {
    // To ensure disabled is not unselected first we need to find values that were changed,
    // then find all disabled values in that change and after that concat them with the rest.
    const value = nextValue.concat(
      differenceBy(selectedValue, nextValue, "value").filter((option) =>
        getOptionDisabled?.(option),
      ),
    );

    onChange?.(value);
  };

  const deselectAll = () => {
    // To preserve disabled items we need to separate them from selected options and set them
    const value = selectedValue.filter((option) => getOptionDisabled?.(option));

    onChange?.(value);
  };

  const selectAll = () => {
    // First we need to separate disabled selected options and then concat them with all the rest selected
    const value = selectedValue
      .filter((option) => getOptionDisabled?.(option))
      .concat(
        comboBoxProps.options.filter((option) => !getOptionDisabled?.(option)),
      );

    onChange?.(value);
  };

  const ListBoxComponent = forwardRef<HTMLUListElement, ListProps>(
    ({ children, key, ...listProps }, ref) => {
      const selectedCount = selectedValue.length;

      const allOptionsAreSelected =
        comboBoxProps.options.length === selectedCount;

      const someOptionsAreSelected = selectedCount > 0;

      const selectAllText = allOptionsAreSelected
        ? deselectAllLabelText
        : selectAllLabelText;

      const selectAllLabel = showSelectAllCount
        ? `${selectAllText} ${comboBoxProps.label} (${selectedCount})`
        : `${selectAllText}`;

      const toggleSelectAll = () => {
        const isAllPossibleOptionsSelected =
          selectedValue.filter((option) => !getOptionDisabled?.(option))
            .length ===
          comboBoxProps.options.filter((option) => !getOptionDisabled?.(option))
            .length;

        if (isAllPossibleOptionsSelected) {
          deselectAll();
        } else {
          selectAll();
        }
      };

      return (
        <List key={key} {...listProps} ref={ref}>
          {selectAllOption && (
            <ListItem
              key={SELECT_ALL_CHECKBOX_OPTION}
              className="MuiAutocomplete-option"
            >
              <div
                role="option"
                tabIndex={-1}
                aria-selected={allOptionsAreSelected}
              >
                <FormControlLabel
                  label={selectAllLabel}
                  control={
                    <Checkbox
                      checked={allOptionsAreSelected}
                      indeterminate={
                        someOptionsAreSelected && !allOptionsAreSelected
                      }
                      onChange={toggleSelectAll}
                    />
                  }
                />
              </div>
            </ListItem>
          )}
          {children}
        </List>
      );
    },
  );

  return (
    <Autocomplete
      disableCloseOnSelect
      data-testid="autocomplete"
      data-qaid="autocomplete"
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, currentValue) =>
        option.value === currentValue.value
      }
      renderOption={Option}
      multiple
      onChange={handleChange}
      renderInput={(params) => (
        <TextField
          error={error}
          helperText={helperText}
          ref={ref}
          label={comboBoxProps.label}
          required={autocompleteProps.required}
          {...params}
          {...textFieldProps}
        />
      )}
      renderValue={(currentValue: readonly TComboBoxOption[], getTagProps) =>
        currentValue
          .filter((opt) => !opt?.disabled)
          .map((option: TComboBoxOption, index: number) => (
            <Chip
              variant="outlined"
              color="default"
              label={option.label}
              {...getTagProps({ index })}
              disabled={getOptionDisabled?.(option)}
              key={`chip-${option.value}`}
            />
          ))
      }
      value={selectedValue}
      getOptionDisabled={getOptionDisabled}
      {...autocompleteProps}
      slots={{
        listbox: ListBoxComponent,
      }}
    />
  );
});

export default ComboBox;
